import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import Analytics from '@pkg/analytics/Analytics';
import Event from '@pkg/analytics/Event';
import useDesignsMutate from '@pkg/entities/designs/mutations/useMutate';
import combine from '@pkg/entities/designs/mutations/utils/combine';

const DELAY = 4000;
const MAX_DELAY = 8000;
const INITIAL_STATE = {
  designId: null,
  state: null,
  events: [],
  level: null,
  oldest: null,
  timeout: null,
};

/**
 * @param {Object} input
 * @param {String} input.designId
 * @param {Object} input.state
 * @returns {Object}
 */
export default function useMutate({ designId, state }) {
  const onMutate = useDesignsMutate();
  const bus = useRef(INITIAL_STATE);
  const router = useRouter();

  /**
   * Keep track of current design id and level
   */
  useEffect(() => {
    bus.current.designId = designId;
    bus.current.state = state;
  }, [designId, state]);

  /**
   * Handle mutation
   */
  function handle() {
    clearTimeout(bus.current.timeout);
    bus.current.oldest = null;
    bus.current.timeout = null;

    const events = Array.from(bus.current.events);
    bus.current.events = [];

    console.debug('Design.mutate.handle', { events });
    if (!events.length) {
      return;
    }

    const mutation = combine(events);
    console.debug('Design.mutate.handle', { mutation });
    return onMutate(bus.current.designId, mutation);
  }

  /**
   * Emit mutation events
   */
  function emit(mutation, action) {
    clearTimeout(bus.current.timeout);
    bus.current.events.push(mutation);
    bus.current.timeout = null;

    // track the event
    console.debug('Design.mutate.emit', { action, mutation });
    Analytics.track(Event.DESIGN_MUTATED, {
      action: action.replace('.', '_'),
      design_id: designId,
    });

    // determine timestamps
    const now = Date.now();
    const oldest = bus.current.oldest ?? now;
    const timeout = oldest + MAX_DELAY;
    bus.current.oldest = oldest;

    // handle immediately
    if (timeout <= now) return handle();

    // timeout handler for smallest remaining time
    const remaining = timeout - now;
    bus.current.timeout = setTimeout(handle, Math.min(remaining, DELAY));
  }

  /**
   * Fire mutations immediately if changing route
   */
  useEffect(() => {
    router.events.on('routeChangeStart', handle);
    return () => router.events.off('routeChangeStart', handle);
  }, []);

  const emitCallback = useCallback(emit, []);
  const flush = useCallback(() => handle(), []);

  return useMemo(() => ({ emit: emitCallback, flush }), [emitCallback, flush]);
}
